import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { stringToCamelCase } from '@/services';
import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';
import type { TDocumentsOnModeration } from '@/types/uploadFiles';

import {
  COLLAPSE_VALID_FILES,
  fileDict,
  type fileDictType
} from '../constants/files.dicts';
import type { FormType } from '../types/form-types';

interface IProps {
  documentsOnModeration: TDocumentsOnModeration[];
}

const useEurasForm = ({ documentsOnModeration }: IProps) => {
  const { attachedFiles } = useAppSelector((state) => state.financingStep);

  const methods = useForm<FormType>({
    mode: 'all',
    delayError: 200
  });

  const [files, setFiles] = useState<Record<string, TFileAttached>>();
  const [filesOnModeration, setFilesOnModeration] =
    useState<Record<string, TFileAttached>>();

  const attachedCollapseFiles = useMemo(
    () =>
      attachedFiles
        ?.map((item) => ({
          ...item,
          type: stringToCamelCase(item.type)
        }))
        .filter((item, index, self) => {
          const isFirstOccurrence =
            self.findIndex(
              (t) => t.name === item.name && t.type === item.type
            ) === index;
          const isValidType = !COLLAPSE_VALID_FILES.includes(item.type);
          const isHasTitle = !!fileDict[item.type as fileDictType];

          return isFirstOccurrence && isValidType && isHasTitle;
        }) || [],
    [attachedFiles]
  );

  useEffect(() => {
    const filesObject = {} as Record<string, TFileAttached>;
    const modifyFilesObject = {} as Record<string, TFileAttached>;

    attachedFiles.forEach((file) => {
      const key = stringToCamelCase(file.type);
      const shouldModify = documentsOnModeration?.some(({ type }) => {
        const convertedType = stringToCamelCase(type);
        return convertedType === key;
      });
      if (!shouldModify) filesObject[key] = file;
      else modifyFilesObject[key] = file;
    });

    setFiles(filesObject);
    setFilesOnModeration(modifyFilesObject);
  }, [attachedFiles, documentsOnModeration]);

  return { methods, files, filesOnModeration, attachedCollapseFiles };
};

export default useEurasForm;
